import { FC, useId, useRef } from "react";
import ReactDatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon, DatePickerIcon, TickIcon } from "../../assets/icons";
import clsx from "clsx";
import { createPortal } from "react-dom";

type Props = {
  onChange: (date: Date) => void;
  onBlur?: () => void;
  selectedDate?: Date;
  error?: string;
  className?: string;
  min?: Date;
  max?: Date;
  showDatePickerIcon?: boolean;
  showCalenderIcon?: boolean;
  tick?: boolean;
  label?: string;
  placeholderText?: string;
  textAlignment?: "right" | "left" | "center";
  dateFormat?: string;
  labelClassName?: string;
  iconClassName?: string;
};

const DatePicker: FC<Props> = ({
  selectedDate,
  onChange,
  onBlur,
  error,
  className = "",
  min,
  max,
  showDatePickerIcon = true,
  showCalenderIcon = false,
  tick = false,
  label,
  placeholderText = "Select date",
  textAlignment = "right",
  dateFormat = "dd/MM/yyyy",
  labelClassName,
  iconClassName,
}) => {
  const ref = useRef<any>(null);
  const id = useId();

  return (
    <div className="cx-flex cx-flex-col">
      <div
        className={clsx(
          "cx-w-full cx-rounded-base cx-border-2 cx-bg-background-default cx-flex cx-items-center cx-relative ",
          {
            "cx-border-stroke-primary": !error,
            "cx-border-stroke-error": !!error,
            "cx-pl-3 cx-gap-2 cx-py-4 cx-pr-3": !label,
            [className]: !!className,
            "cx-pr-2": showCalenderIcon,
          }
        )}
      >
        {showDatePickerIcon && (
          <div
            className={clsx({
              "cx-pl-3": label,
              "cx-flex cx-items-center": label,
            })}
          >
            <DatePickerIcon
              className={`cx-text-text-brand cx-w-6 cx-h-6 ${iconClassName}`}
              onClick={() => {
                ref.current?.toggleCalendar();
              }}
            />
          </div>
        )}
        <ReactDatePicker
          id={id}
          className={clsx(
            "cx-bg-transparent cx-appearance-none cx-outline-none cx-w-full cx-rounded-base",
            {
              "cx-placeholder-transparent cx-peer cx-p-4 focus:cx-pb-2 focus:cx-pt-6 [&.react-datepicker__input-container>input:not([value=''])]:cx-pt-6 [&.react-datepicker__input-container>input:not([value=''])]:cx-pb-2":
                label,
              [`cx-text-${textAlignment}`]: !label,
              "placeholder:cx-text-s": !label,
              "cx-pr-1": showCalenderIcon,
            }
          )}
          selected={selectedDate}
          onChange={(date) => onChange(date!)}
          onBlur={onBlur}
          placeholderText={label || placeholderText}
          startDate={new Date()}
          dateFormat={dateFormat}
          ref={ref as any}
          wrapperClassName="cx-w-full"
          popperClassName="cx-z-[100]"
          minDate={min}
          maxDate={max}
          showIcon={false}
          popperContainer={({ children }) =>
            createPortal(children, document.body)
          }
        />

        {label && (
          <label
            htmlFor={id}
            className={clsx(
              "cx-absolute cx-transition-all cx-pointer-events-none cx-px-4",
              "cx-text-[15px] cx-font-normal cx-text-gray-400",
              {
                "cx-pt-4 cx-pb-2 cx-left-0 -cx-top-3": selectedDate,
                "cx-text-text-error": !!error,
                "cx-text-text-tertiary": !error && !labelClassName,
              },
              labelClassName
            )}
            style={{
              left: showDatePickerIcon ? "48px" : "0",
            }}
          >
            {label}
          </label>
        )}

        {showCalenderIcon && (
          <div
            className={clsx({
              "cx-pr-2": label,
            })}
          >
            <CalendarIcon
              className="cx-w-6 cx-h-6 cx-cursor-pointer"
              stroke="#0064FF"
              onClick={() => {
                ref.current?.setFocus();
              }}
            />
          </div>
        )}

        {tick && (
          <div>
            <TickIcon height="100%" className="cx-stroke-success-regular" />
          </div>
        )}
      </div>
      {error && (
        <div className="cx-text-xs cx-py-1 cx-text-text-error">{error}</div>
      )}
    </div>
  );
};

export default DatePicker;
